import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { KeyRound, Loader2, Mail, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function ChangePasswordPage() {
    const { user, changePassword, requestPasswordCode, verifyPasswordCode } = useAuth();
    const navigate = useNavigate();

    const mustChange = !!user?.must_change_password;
    const hasEmail = !!user?.email;

    const [step, setStep] = useState('form'); // 'form' | 'code'
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [emailHint, setEmailHint] = useState('');
    const [loading, setLoading] = useState(false);

    const goHome = () => navigate(user?.role === 'admin' ? '/admin' : '/client');

    const validatePasswords = () => {
        if (newPassword !== confirmPassword) {
            toast.error('Le password non corrispondono');
            return false;
        }
        if (newPassword.length < 6) {
            toast.error('La password deve essere di almeno 6 caratteri');
            return false;
        }
        return true;
    };

    // Step 1 — invio del codice (o cambio diretto al primo accesso).
    const handleRequest = async (e) => {
        e.preventDefault();
        if (!validatePasswords()) return;

        // Primo accesso (deve impostare password + email di recupero): cambio diretto.
        if (mustChange) {
            if (!email) {
                toast.error('Inserisci un\'email per il recupero password');
                return;
            }
            setLoading(true);
            try {
                await changePassword(newPassword, email);
                toast.success('Password aggiornata con successo!');
                goHome();
            } catch (err) {
                toast.error(err.response?.data?.detail || 'Errore nel cambio password');
            } finally {
                setLoading(false);
            }
            return;
        }

        // Cambio volontario: serve un'email di riferimento per ricevere il codice.
        if (!hasEmail && !email) {
            toast.error('Imposta un\'email di recupero per ricevere il codice di sicurezza');
            return;
        }
        setLoading(true);
        try {
            const data = await requestPasswordCode(newPassword, hasEmail ? null : email);
            if (data?.demo_bypass) {
                toast.success('Password aggiornata con successo!');
                goHome();
                return;
            }
            setEmailHint(data?.email_hint || '');
            setStep('code');
            toast.success('Ti abbiamo inviato un codice di sicurezza via email');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Errore nell\'invio del codice');
        } finally {
            setLoading(false);
        }
    };

    // Step 2 — verifica del codice e applicazione della nuova password.
    const handleVerify = async (e) => {
        e.preventDefault();
        if (!code.trim()) {
            toast.error('Inserisci il codice di sicurezza');
            return;
        }
        setLoading(true);
        try {
            await verifyPasswordCode(code.trim());
            toast.success('Password aggiornata con successo!');
            goHome();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Codice non valido');
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setLoading(true);
        try {
            const data = await requestPasswordCode(newPassword, hasEmail ? null : email);
            setEmailHint(data?.email_hint || emailHint);
            toast.success('Nuovo codice inviato');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Errore nell\'invio del codice');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#F2F5F2] p-4">
            <Card className="w-full max-w-md border border-[#CBD5E1] shadow-none">
                <CardHeader className="space-y-1">
                    <div className="flex items-center gap-2 mb-2">
                        {step === 'code'
                            ? <ShieldCheck className="h-8 w-8 text-[#1B4E8E]" />
                            : <KeyRound className="h-8 w-8 text-[#1B4E8E]" />}
                    </div>
                    <CardTitle className="text-2xl text-[#0F172A]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                        {step === 'code'
                            ? 'Conferma con il codice'
                            : (mustChange ? 'Imposta nuova password' : 'Cambia password')}
                    </CardTitle>
                    <CardDescription className="text-[#64748B]">
                        {step === 'code'
                            ? `Inserisci il codice a 6 cifre inviato a ${emailHint || 'la tua email'}.`
                            : (mustChange
                                ? 'Per motivi di sicurezza, devi impostare una nuova password e un\'email di recupero.'
                                : 'Per completare il cambio password ti invieremo un codice di sicurezza via email.')}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {step === 'form' ? (
                        <form onSubmit={handleRequest} className="space-y-4">
                            {(mustChange || !hasEmail) && (
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-[#0F172A] flex items-center gap-2">
                                        <Mail className="h-4 w-4" />
                                        Email di recupero
                                    </Label>
                                    <Input
                                        id="email"
                                        data-testid="change-password-email-input"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="tua@email.com"
                                        required
                                        className="border-[#CBD5E1] focus:ring-[#1B4E8E]"
                                    />
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label htmlFor="newPassword" className="text-[#0F172A]">Nuova password</Label>
                                <Input
                                    id="newPassword"
                                    data-testid="change-password-new-input"
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Inserisci nuova password"
                                    required
                                    className="border-[#CBD5E1] focus:ring-[#1B4E8E]"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword" className="text-[#0F172A]">Conferma password</Label>
                                <Input
                                    id="confirmPassword"
                                    data-testid="change-password-confirm-input"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Conferma nuova password"
                                    required
                                    className="border-[#CBD5E1] focus:ring-[#1B4E8E]"
                                />
                            </div>
                            <Button
                                type="submit"
                                data-testid="change-password-submit-btn"
                                className="w-full bg-[#1B4E8E] hover:bg-[#143A6B] text-white"
                                disabled={loading}
                            >
                                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                {mustChange ? 'Salva password' : 'Invia codice di sicurezza'}
                            </Button>
                        </form>
                    ) : (
                        <form onSubmit={handleVerify} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="code" className="text-[#0F172A]">Codice di sicurezza</Label>
                                <Input
                                    id="code"
                                    data-testid="change-password-code-input"
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={6}
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                                    placeholder="______"
                                    required
                                    className="border-[#CBD5E1] focus:ring-[#1B4E8E] text-center text-2xl tracking-[0.5em] font-semibold"
                                />
                            </div>
                            <Button
                                type="submit"
                                data-testid="change-password-verify-btn"
                                className="w-full bg-[#1B4E8E] hover:bg-[#143A6B] text-white"
                                disabled={loading}
                            >
                                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                Conferma e cambia password
                            </Button>
                            <div className="flex items-center justify-between text-sm">
                                <button
                                    type="button"
                                    onClick={() => { setStep('form'); setCode(''); }}
                                    className="text-[#64748B] hover:underline"
                                    data-testid="change-password-back-btn"
                                >
                                    Indietro
                                </button>
                                <button
                                    type="button"
                                    onClick={handleResend}
                                    disabled={loading}
                                    className="text-[#1B4E8E] hover:underline disabled:opacity-50"
                                    data-testid="change-password-resend-btn"
                                >
                                    Reinvia codice
                                </button>
                            </div>
                        </form>
                    )}

                    {!mustChange && step === 'form' && (
                        <Button
                            variant="ghost"
                            onClick={goHome}
                            className="w-full mt-2 text-[#64748B]"
                            data-testid="change-password-cancel-btn"
                        >
                            Annulla
                        </Button>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
